import { watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMutationApp } from 'hooks/apolloQuery.js'
import { useLoyaltyStore } from 'stores/loyalty/index.js'
import { useNotifyQueue } from 'stores/loyalty/notify.js'
import { errorHandling, notification } from 'src/core/hooks/notifications'
import requestLpReward from '@/apollo/mutations/requestLpReward.gql'

const GAME_ROUTE_NAME = 'GamePageNew'
const TOAST_TIMEOUT = 10000

function parseProps(props) {
  if (typeof props === 'string') {
    try {
      return JSON.parse(props)
    } catch {
      return null
    }
  }
  return props && typeof props === 'object' ? props : null
}

export function useAchievementNotification() {
  const route = useRoute()
  const { t, locale } = useI18n()
  const { mutate } = useMutationApp(requestLpReward)
  const loyaltyStore = useLoyaltyStore()
  const notifyQueue = useNotifyQueue()

  const shownAchievementIds = new Set()

  const isInGame = () => route.name === GAME_ROUTE_NAME

  function getAchievementTitle(propsContent) {
    const list = Array.isArray(propsContent) ? propsContent : [propsContent]

    for (const item of list) {
      const title = parseProps(item?.props)?.title
      if (!title) {
        continue
      }
      if (typeof title === 'string') {
        return title
      }
      return title[locale.value] ?? title.en ?? null
    }

    return null
  }

  function getMessage(content) {
    const title = getAchievementTitle(content?.propsContent)
    const level = content?.achievementLevel
    return [title, level ? t('achievementLevelLabel', { level }) : null].filter(Boolean).join(' · ')
  }

  function claimReward(content) {
    return mutate({ taskId: Number(content.taskID) })
      .then((res) => {
        notification({ type: 'success', title: t('rewardHasBeenReceived') })

        const achievement = res?.data?.requestReward?.achievement
        if (achievement && loyaltyStore.achievementsData.length) {
          loyaltyStore.updateAchievement(achievement)
          if (content.achievementLevel === 1) {
            loyaltyStore.incrementDoneAchievements()
          }
        }
        loyaltyStore.getLpLevelsQuery?.refetch?.()
      })
      .catch(errorHandling)
  }

  function showAchievementToast(content) {
    notification({
      type: 'achievement',
      iconName: 'medal-new',
      title: t('achievementReceived'),
      text: getMessage(content),
      timer: TOAST_TIMEOUT,
      actions: [
        {
          label: t('takeReward'),
          variant: 'primary',
          handler: () => claimReward(content),
        },
      ],
    })
  }

  function flushQueue() {
    notifyQueue.drain().forEach(showAchievementToast)
  }

  function handleAchievementNotification(content) {
    const isAchievementEarned = content?.isCompleted === true && content?.rewardClaimed === false
    if (!isAchievementEarned || !content?.propsContent) {
      return
    }

    if (content.id != null) {
      if (shownAchievementIds.has(content.id)) {
        return
      }
      shownAchievementIds.add(content.id)
    }

    if (isInGame()) {
      notifyQueue.enqueue(content)
    } else {
      showAchievementToast(content)
    }
  }

  function init() {
    notifyQueue.load()

    if (!isInGame()) {
      flushQueue()
    }

    watch(
      () => route.name,
      (newName, oldName) => {
        if (oldName === GAME_ROUTE_NAME && newName !== GAME_ROUTE_NAME) {
          flushQueue()
        }
      },
    )
  }

  return { handleAchievementNotification, init, showAchievementToast }
}
